import { Grid, Typography, Box, Skeleton } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { fetchUserMeetingService, UserWeekMenu, FetchUserMeetingServiceResponse } from "../../../weekMenuApi"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { MeetingCard } from "./MeetingCard"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { isEmpty } from "lodash"
import { DateTime } from "luxon"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarCheck } from "@fortawesome/pro-solid-svg-icons"
import { selectUserId } from "../../../../../../login/loginSelectors"
import { QueryErrorBoundary } from "../../../../../../../core/api/components/QueryErrorBoundary"
import { ErrorBoundary } from "react-error-boundary"
import { UserDashboardErrorFallbackCard } from "../../UserDashboardErrorFallbackCard"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"

const useStyles = makeStyles()((theme) => ({
    column: {
        paddingTop: "2rem !important",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    noDataContainer: {
        padding: "1.5rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        background: theme.palette.common.white,
        borderRadius: ".625rem",
        textAlign: "center",
        boxShadow: "0px 2px 6px 0px #00000014",
    },
    noMeetingIcon: {
        fontSize: "4.5rem",
        color: tailwindPalette.purple[900],
        backgroundColor: tailwindPalette.purple[200],
        borderRadius: ".5rem",
        padding: ".75rem",
        marginBottom: "1rem",
    },
    skeletonCard: {
        height: "20rem",
        borderRadius: ".625rem",
    },
}))

const transform = (data: FetchUserMeetingServiceResponse) => {
    return data.orders
}

function SafeUserMeetings() {
    const { classes } = useStyles()
    const userId = useAppSelector(selectUserId)
    const startDate = DateTime.now().startOf("week").toISODate()
    const endDate = DateTime.now().endOf("week").toISODate()

    const { data: meetings, isLoading } = useApiQuery(fetchUserMeetingService, {
        queryName: UserWeekMenu.MEETING_SERVICE_LIST,
        dependencies: {
            userId: userId ?? 0,
            view: "dashboard",
        },
        useErrorBoundary: true,
        enabled: Boolean(userId),
        select: transform,
    })

    return (
        <Grid item xs={12} md={6} className={classes.column}>
            <LocalizedStrict id="user-home-upcoming-meetings">
                <Typography variant="h6" fontWeight="bold">
                    Meetings
                </Typography>
            </LocalizedStrict>
            {isLoading ? (
                <>
                    <Skeleton variant="rectangular" className={classes.skeletonCard} />
                    <Skeleton variant="rectangular" className={classes.skeletonCard} />
                </>
            ) : !isEmpty(meetings) ? (
                meetings?.map((meeting) => (
                    <ErrorBoundary fallback={<UserDashboardErrorFallbackCard />}>
                        <MeetingCard key={meeting.id} meeting={meeting} meetings={meetings} />
                    </ErrorBoundary>
                ))
            ) : (
                <NoDataCard />
            )}
        </Grid>
    )
}

export function UserMeetings() {
    return (
        <QueryErrorBoundary Fallback={NoDataFallback}>
            <SafeUserMeetings />
        </QueryErrorBoundary>
    )
}

function NoDataFallback() {
    const { classes } = useStyles()
    return (
        <Grid item xs={12} md={6} className={classes.column}>
            <LocalizedStrict id="user-home-upcoming-meetings">
                <Typography variant="h6" fontWeight="bold">
                    Meetings
                </Typography>
            </LocalizedStrict>
            <NoDataCard />
        </Grid>
    )
}

function NoDataCard() {
    const { classes } = useStyles()
    return (
        <Box className={classes.noDataContainer}>
            <FontAwesomeIcon icon={faCalendarCheck} className={classes.noMeetingIcon} />
            <LocalizedStrict id="user-home-upcoming-meetings-no-meetings">
                <Typography variant="subtitle1" color="#00000099" textAlign="left">
                    No upcoming meetings
                </Typography>
            </LocalizedStrict>
        </Box>
    )
}
